// Immutable mutation helpers for the (flat) block tree.
//
// The canvas drives a single source of truth (BlockTree). Every interaction
// produces a new tree via these helpers — never mutate.

import { nanoid } from 'nanoid';

import { Block, BlockTree, TEMPLATE_BLOCKS_VERSION } from '@/types';

export function newBlockId(): string {
  return nanoid(8);
}

// ---------- find ----------

export function findBlock(tree: BlockTree, id: string): Block | null {
  return tree.blocks.find((b) => b.id === id) ?? null;
}

export function findBlockIndex(tree: BlockTree, id: string): number {
  return tree.blocks.findIndex((b) => b.id === id);
}

// ---------- update / remove / insert / move ----------

export function updateBlock(
  tree: BlockTree,
  id: string,
  produce: (block: Block) => Block
): BlockTree {
  return {
    ...tree,
    blocks: tree.blocks.map((b) => (b.id === id ? produce(b) : b)),
  };
}

export function removeBlock(tree: BlockTree, id: string): BlockTree {
  return {
    ...tree,
    blocks: tree.blocks.filter((b) => b.id !== id),
  };
}

export function insertBlock(tree: BlockTree, index: number, block: Block): BlockTree {
  const clamped = Math.max(0, Math.min(index, tree.blocks.length));
  const next = [...tree.blocks];
  next.splice(clamped, 0, block);
  return { ...tree, blocks: next };
}

export function appendBlock(tree: BlockTree, block: Block): BlockTree {
  return { ...tree, blocks: [...tree.blocks, block] };
}

export function moveBlock(tree: BlockTree, id: string, toIndex: number): BlockTree {
  const fromIndex = findBlockIndex(tree, id);
  if (fromIndex === -1) {
    return tree;
  }
  const next = [...tree.blocks];
  const [moved] = next.splice(fromIndex, 1);
  // Adjust target index for the removal if moving forwards.
  const adjusted = toIndex > fromIndex ? toIndex - 1 : toIndex;
  const clamped = Math.max(0, Math.min(adjusted, next.length));
  next.splice(clamped, 0, moved);
  return { ...tree, blocks: next };
}

// ---------- factories ----------

export function defaultBlockTree(): BlockTree {
  // Mirrors the spirit of the backend default template: a subject heading and
  // an executive-summary block. Authors can add Section blocks per attached
  // ScopeComponent from the palette.
  return {
    version: TEMPLATE_BLOCKS_VERSION,
    blocks: [
      {
        id: newBlockId(),
        kind: 'paragraph',
        content: [{ type: 'var', path: 'executive_summary', filters: [] }],
      },
      { id: newBlockId(), kind: 'divider' },
      {
        id: newBlockId(),
        kind: 'heading',
        level: 1,
        content: [{ type: 'var', path: 'subject.subject_name', filters: [] }],
      },
    ],
  };
}
